import { useEffect, useState } from 'react';
import { api } from '../services/api.js';
import ClassificationSelect from './ClassificationSelect.jsx';
/**
 * Classification fields for the item editor: a cascading category →
 * subcategory pair plus a tag picker. Fetches the merged taxonomy
 * (shared base + user's private additions) on mount and lets the user
 * add new categories, subcategories, and tags inline.
 *
 * Controlled component — parent owns the values:
 *   <ClassificationFields
 *     category={form.category}
 *     subcategory={form.subcategory}
 *     tags={form.tags}
 *     onCategory={(v) => setField('category', v)}
 *     onSubcategory={(v) => setField('subcategory', v)}
 *     onTags={(v) => setField('tags', v)}
 *   />
 */
export default function ClassificationFields({
  category, subcategory, tags = [],
  onCategory, onSubcategory, onTags,
}) {
  const [taxonomy, setTaxonomy] = useState({ categories: {}, tags: [] });
  const [loading, setLoading] = useState(true);

  // Inline-add UI state
  const [addingCategory, setAddingCategory] = useState(false);
  const [addingSub, setAddingSub] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newSub, setNewSub] = useState('');
  const [newTag, setNewTag] = useState('');
  const [busy, setBusy] = useState(false);

  const customCats = taxonomy.customCategories ?? {};
  const customTagSet = new Set((taxonomy.customTags ?? []).map((t) => t.toLowerCase()));
  const isCustomCat = (c) => Object.prototype.hasOwnProperty.call(customCats, c);
  const isCustomSub = (c, s) => (customCats[c] ?? []).includes(s);
  const isCustomTag = (t) => customTagSet.has(t.toLowerCase());
  
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    api.getCategories()
      .then(setTaxonomy)
      .catch(() => setTaxonomy({ categories: {}, tags: [] }))
      .finally(() => setLoading(false));
  }, []);

  const categoryNames = Object.keys(taxonomy.categories);
  const subOptions = category ? (taxonomy.categories[category] ?? []) : [];

  async function confirmAddCategory() {
    const name = newCategory.trim();
    if (!name) { setAddingCategory(false); return; }
    setBusy(true);
    try {
      const updated = await api.addCategory(name);
      setTaxonomy(updated);
      onCategory(name);
      onSubcategory('');
    } catch { /* ignore; keep current */ }
    finally {
      setBusy(false);
      setAddingCategory(false);
      setNewCategory('');
    }
  }

  async function confirmAddSub() {
    const name = newSub.trim();
    if (!name || !category) { setAddingSub(false); return; }
    setBusy(true);
    try {
      const updated = await api.addCategory(category, name);
      setTaxonomy(updated);
      onSubcategory(name);
    } catch { /* ignore */ }
    finally {
      setBusy(false);
      setAddingSub(false);
      setNewSub('');
    }
  }

  function toggleTag(t) {
    if (tags.includes(t)) onTags(tags.filter((x) => x !== t));
    else onTags([...tags, t]);
  }
  
  async function tryDeleteCategory(cat) {
    setBusy(true); setNotice(null);
    try {
      const { taxonomy: updated } = await api.deleteCategory(cat);
      setTaxonomy(updated);
      if (category === cat) { onCategory(''); onSubcategory(''); }
    } catch (e) {
      setNotice(e.message);   // "Category is used by N items. Reassign them first."
    } finally { setBusy(false); }
  }
  async function tryDeleteSub(cat, sub) {
    setBusy(true); setNotice(null);
    try {
      const { taxonomy: updated } = await api.deleteSubcategory(cat, sub);
      setTaxonomy(updated);
      if (subcategory === sub) onSubcategory('');
    } catch (e) { setNotice(e.message); }
    finally { setBusy(false); }
  }
  async function tryDeleteTag(tag) {
    setBusy(true); setNotice(null);
    try {
      const { taxonomy: updated } = await api.deleteTag(tag);
      setTaxonomy(updated);
      if (tags.includes(tag)) onTags(tags.filter((t) => t !== tag));
    } catch (e) { setNotice(e.message); }
    finally { setBusy(false); }
  }

  async function confirmAddTag() {
    const name = newTag.trim();
    if (!name) return;
    // Already known? just select it.
    const existing = taxonomy.tags.find((t) => t.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!tags.includes(existing)) onTags([...tags, existing]);
      setNewTag('');
      return;
    }
    setBusy(true);
    try {
      const updated = await api.addTag(name);
      setTaxonomy(updated);
      onTags([...tags, name]);
    } catch { /* ignore */ }
    finally {
      setBusy(false);
      setNewTag('');
    }
  }

  if (loading) {
    return <div className="field"><div className="muted tiny">Loading categories…</div></div>;
  }

  return (
    <>
      <div className="field-row">
        {/* Category */}
        <div className="field">
          <label>Category</label>
          {addingCategory ? (
            <div className="inline-add">
              <input
                autoFocus
                value={newCategory}
                maxLength={60}
                placeholder="New category name"
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmAddCategory(); } }}
              />
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={confirmAddCategory}>Add</button>
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => { setAddingCategory(false); setNewCategory(''); }}>Cancel</button>
            </div>
          ) : (
            <ClassificationSelect
              value={category}
              options={Object.keys(taxonomy.categories)}
              isCustom={(name) => isCustomCat(name)}
              onSelect={(name) => { onCategory(name); onSubcategory(''); }}
              onDelete={(name) => tryDeleteCategory(name)}
              onAddNew={() => setAddingCategory(true)}
              placeholder="— none —"
              busy={busy}
            />
          )}
        </div>

        {/* Subcategory — only when a category is chosen */}
        {category && !addingCategory && (
          <div className="field">
            <label>Subcategory</label>
            {addingSub ? (
              <div className="inline-add">
                <input
                  autoFocus
                  value={newSub}
                  maxLength={60}
                  placeholder="New subcategory name"
                  onChange={(e) => setNewSub(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmAddSub(); } }}
                />
                <button type="button" className="btn btn-ghost" disabled={busy} onClick={confirmAddSub}>Add</button>
                <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => { setAddingSub(false); setNewSub(''); }}>Cancel</button>
              </div>
            ) : (
              <ClassificationSelect
                value={subcategory}
                options={taxonomy.categories[category] ?? []}
                isCustom={(name) => isCustomSub(category, name)}
                onSelect={(name) => onSubcategory(name)}
                onDelete={(name) => tryDeleteSub(category, name)}
                onAddNew={() => setAddingSub(true)}
                placeholder="— none —"
                busy={busy}
              />
            )}
          </div>
        )}
      </div>

      {/* Tags */}
      <div className="field">
        <label>Tags</label>
        <div className="chips mb-10">
          {taxonomy.tags.map((t) => {
            const on = tags.includes(t);
            const custom = isCustomTag(t);
            return (
              <span key={t} className="chip-wrap">
                <button
                  type="button"
                  onClick={() => toggleTag(t)}
                  aria-pressed={on}
                  className={`chip${on ? ' is-on' : ''}`}
                >
                  {t}
                </button>
                {custom && (
                  <button
                    type="button"
                    className="chip-x"
                    title="Delete this custom tag"
                    aria-label={`Delete custom tag "${t}"`}
                    onClick={() => tryDeleteTag(t)}
                    disabled={busy}
                  >✕</button>
                )}
              </span>
            );
          })}
          {/* Any selected tags not in the suggested list (already-custom) */}
          {tags.filter((t) => !taxonomy.tags.includes(t)).map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed="true"
              onClick={() => toggleTag(t)}
              className="chip is-on"
            >
              {t}
            </button>
          ))}
        </div>
        <div className="inline-add">
          <input
            value={newTag}
            maxLength={40}
            placeholder="Add a tag…"
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmAddTag(); } }}
          />
          <button type="button" className="btn btn-ghost" disabled={busy || !newTag.trim()} onClick={confirmAddTag}>Add tag</button>
        </div>
        <div className="hint">Tags group items across categories — e.g. "Travel" for things you take on trips.</div>
      </div>
      {notice && <div className="muted tiny notice-danger" role="alert">{notice}</div>}
    </>
  );
}
